import { BotaoArcade } from './BotaoArcade';
import { desenharPainelEscuro } from './cenario';
import { GAME, TEX } from '../config/constants';
import { ARCADE, ARCADE_HEX, ARCADE_TEXT } from '../config/theme';

const PAINEL = { x: 130, y: 82, largura: 1020, altura: 637 };
const VAO = 28;
const TAMANHOS = [30, 27, 24, 22];
/** Botão secundário (voltar): contorno branco sobre o grafite, para não competir com o botão verde de ação. */
const CORES_VOLTAR = { fundo: ARCADE_HEX.GRAFITE, fio: ARCADE_HEX.BRANCO, fioMeio: ARCADE_HEX.GRAFITE };
/** Folga entre os dois botões quando o modal tem o de ação e o de voltar lado a lado. */
const FOLGA_ENTRE_BOTOES = 32;

/**
 * Modal das fases (Figma, quadros 40, 41 e 36): painel escuro de borda
 * dupla, título em pixel, blocos de conteúdo centrados e um botão.
 *
 * Blocos aceitos:
 * - `{ texto, peso?, maiusculas?, italico?, cor?, largura?, tamanho? }` parágrafo em Barlow
 * - `{ pixel }` linha(s) em Silkscreen
 * - `{ gigante }` uma palavra em Silkscreen grande (nome do sorteado)
 * - `{ imagem }` chave de textura
 * - `{ vazio }` espaço em branco com essa altura
 * - `{ antes, imagem, depois }` frase com uma imagem no meio da primeira linha
 * - `{ legenda: [{ imagem, rotulo, escala? }] }` fileira de ícone + rótulo em pixel
 *
 * Qualquer bloco aceita `vao` (distância ao bloco anterior). No lugar do
 * botão, `toque: { rotulo, aoClicar }` faz o modal inteiro avançar ao toque.
 * `voltar: { rotulo, largura?, aoClicar? }` acrescenta um botão secundário que só fecha o modal (voltar para a
 * tela de baixo, ex.: a pontuação). Sozinho, ocupa o lugar do botão; com `botao`, os dois ficam lado a lado.
 * Se a pilha não couber em `alturaMaxima`, o texto corrido encolhe.
 */
export class ModalArcade {
    constructor (scene, {
        titulo = null, tituloY = 160, blocos = [], botao = null, voltar = null, toque = null, largura = 640,
        painel = PAINEL, centroY = 374, botaoY = 618, alturaMaxima = 352,
        aoAbrir = null, aoFechar = null
    }) {
        this.scene = scene;
        this.aoFechar = aoFechar;
        this.camada = scene.add.container(0, 0).setDepth(300);

        const fundo = scene.add.rectangle(GAME.CENTER_X, GAME.CENTER_Y, GAME.LARGURA_TOTAL, GAME.HEIGHT, 0x000000, 0.72)
            .setInteractive();
        const moldura = scene.add.graphics();
        desenharPainelEscuro(moldura, painel.x, painel.y, painel.largura, painel.altura);
        this.camada.add([fundo, moldura]);
        if (titulo) this.camada.add(scene.add.text(640, tituloY, titulo.toUpperCase(), ARCADE_TEXT.tituloModal).setOrigin(0.5));

        // Monta os blocos, mede a pilha e centra; se passar do limite, refaz menor.
        const vaoDe = (bloco) => bloco.vao ?? VAO;
        let pecas, altura;
        for (const tamanho of TAMANHOS) {
            pecas?.forEach((peca) => peca.objetos.forEach((objeto) => objeto.destroy()));
            pecas = blocos.map((bloco) => this.criarBloco(bloco, largura, tamanho));
            altura = pecas.reduce((soma, peca, i) => soma + peca.altura + (i > 0 ? vaoDe(blocos[i]) : 0), 0);
            if (altura <= alturaMaxima) break;
        }
        let y = centroY - altura / 2;
        pecas.forEach((peca, i) => {
            if (i > 0) y += vaoDe(blocos[i]);
            peca.posicionar(y);
            y += peca.altura;
        });

        // Dois botões lado a lado, afastados do centro pela folga e pela metade do mais largo; um só fica no centro.
        const larguraBotao = botao ? (botao.largura ?? 368) : 0;
        const larguraVoltar = voltar ? (voltar.largura ?? 368) : 0;
        const afastamento = botao && voltar ? Math.max(larguraBotao, larguraVoltar) / 2 + FOLGA_ENTRE_BOTOES / 2 : 0;
        if (voltar) {
            this.camada.add(new BotaoArcade(scene, 640 - afastamento, botaoY, {
                rotulo: voltar.rotulo, largura: larguraVoltar, cores: CORES_VOLTAR, aoClicar: () => {
                    this.fechar();
                    voltar.aoClicar?.();
                }
            }));
        }
        if (botao) {
            this.camada.add(new BotaoArcade(scene, 640 + afastamento, botaoY, {
                rotulo: botao.rotulo, largura: larguraBotao, habilitado: botao.habilitado ?? true, aoClicar: () => {
                    this.fechar();
                    botao.aoClicar?.();
                }
            }));
        } else if (toque) {
            this.camada.add(scene.add.text(640, botaoY + 42, toque.rotulo.toUpperCase(), {
                ...ARCADE_TEXT.pixelModal, color: ARCADE.LIMA
            }).setOrigin(0.5));
            fundo.on('pointerup', () => {
                this.fechar();
                toque.aoClicar?.();
            });
        }
        if (scene.textures.exists(TEX.LOGO_ARGO_X_BRANCO)) {
            this.camada.add(scene.add.image(640, 760, TEX.LOGO_ARGO_X_BRANCO).setDisplaySize(300, 44));
        }

        aoAbrir?.();
    }

    criarBloco (bloco, largura, tamanho = TAMANHOS[0]) {
        const { scene } = this;
        const estiloTexto = { ...ARCADE_TEXT.textoModal, fontSize: `${bloco.tamanho ?? tamanho}px` };
        const peca = (objetos, altura, posicionar) => {
            this.camada.add(objetos);
            return { objetos, altura, posicionar };
        };

        if (bloco.vazio) return peca([], bloco.vazio, () => {});   // espaço reservado (asset ainda não exportado)

        if (bloco.legenda) {
            const itens = bloco.legenda.map(({ imagem, rotulo, escala = 0.7 }) => {
                const icone = scene.add.image(0, 0, imagem).setOrigin(0, 0.5).setScale(escala);
                const texto = scene.add.text(0, 0, rotulo.toUpperCase(), { ...ARCADE_TEXT.pixelModal, fontSize: '16px', fontStyle: '400' })
                    .setOrigin(0, 0.5);
                return { icone, texto, largura: icone.displayWidth + 8 + texto.width };
            });
            const total = itens.reduce((soma, item) => soma + item.largura, 0) + 18 * (itens.length - 1);
            let x = 640 - total / 2;
            itens.forEach((item) => {
                item.icone.x = x;
                item.texto.x = x + item.icone.displayWidth + 8;
                x += item.largura + 18;
            });
            return peca(itens.flatMap((item) => [item.icone, item.texto]), 28, (y) => {
                itens.forEach((item) => {
                    item.icone.setY(y + 14);
                    item.texto.setY(y + 14);
                });
            });
        }

        if (bloco.imagem && bloco.antes === undefined) {
            const imagem = scene.add.image(640, 0, bloco.imagem).setOrigin(0.5, 0);
            return peca([imagem], imagem.height, (y) => imagem.setY(y));
        }

        if (bloco.gigante) {
            const texto = scene.add.text(640, 0, bloco.gigante.toUpperCase(), ARCADE_TEXT.gigante).setOrigin(0.5, 0);
            return peca([texto], texto.height + 30, (y) => texto.setY(y + 15));
        }

        if (bloco.pixel) {
            const texto = scene.add.text(640, 0, bloco.pixel.toUpperCase(), ARCADE_TEXT.pixelModal).setOrigin(0.5, 0);
            return peca([texto], texto.height, (y) => texto.setY(y));
        }

        if (bloco.antes !== undefined) {
            const antes = scene.add.text(0, 0, bloco.antes, estiloTexto).setOrigin(0, 0);
            const imagem = scene.add.image(0, 0, bloco.imagem).setOrigin(0, 0.5);
            const [primeira, ...resto] = bloco.depois.split('\n');
            const depois = scene.add.text(0, 0, primeira, estiloTexto).setOrigin(0, 0);
            const abaixo = scene.add.text(640, 0, resto.join('\n'), estiloTexto).setOrigin(0.5, 0);

            const total = antes.width + 14 + imagem.width + 14 + depois.width;
            antes.x = 640 - total / 2;
            imagem.x = antes.x + antes.width + 14;
            depois.x = imagem.x + imagem.width + 14;
            return peca([antes, imagem, depois, abaixo], antes.height + (resto.length ? abaixo.height : 0), (y) => {
                antes.setY(y);
                depois.setY(y);
                imagem.setY(y + antes.height / 2);
                abaixo.setY(y + antes.height);
            });
        }

        const peso = bloco.peso ?? '500';
        const texto = scene.add.text(640, 0, bloco.maiusculas ? bloco.texto.toUpperCase() : bloco.texto, {
            ...estiloTexto,
            fontStyle: bloco.italico ? 'italic 600' : peso,   // só há itálico no peso 600
            ...(bloco.cor ? { color: bloco.cor } : {}),
            wordWrap: { width: bloco.largura ?? largura }
        }).setOrigin(0.5, 0);
        return peca([texto], texto.height, (y) => texto.setY(y));
    }

    fechar () {
        this.camada.destroy();
        this.aoFechar?.();
    }
}
